/**
 * FoliosTab — Rangos de folios entregados a los terminales (PLAN_POS_IMPLEMENTACION F2).
 *
 * El terminal timbra sin conexión, así que necesita folios propios antes de vender.
 * Asignar es `pos.folios: assign`; revocar es `approve`, porque no se deshace: los
 * folios sin usar quedan quemados y hay que anularlos ante el SII.
 */
import { useMemo, useState } from 'react'
import { Plus, Ban, SlidersHorizontal } from 'lucide-react'
import { usePermission } from '@/hooks/usePermission'
import { useToast, ToastContainer } from '@/components/Toast'
import { Button } from '@/components/Button'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { DataTable } from '@/components/ds/DataTable'
import { Modal } from '@/components/ds/Modal'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'
import { StatusBadge } from '@/components/ds/StatusBadge'
import { POS_FOLIO_RANGE_STATUS } from '@/components/ds/statusCatalogs'
import { EmptyState } from '@/components/EmptyState'
import { extractErrorMessage } from '@/lib/errorUtils'
import { fmtDateTime } from '@/lib/utils'
import {
  usePosFolios, usePosTerminals, usePosFolioPolicies, useAssignFolios, useRevokeFolios,
  useSaveFolioPolicy, type PosFolioRange, type PosFolioPolicy, type FolioPool,
} from '../hooks/usePos'
import { nombreDelTipo } from '../tiposDeDocumento'

const tipo = nombreDelTipo
const fmt = (n: number) => n.toLocaleString('es-CL')

export default function FoliosTab() {
  const { can } = usePermission()
  const toast = useToast()
  const { data, isLoading } = usePosFolios()
  const { data: policies = [] } = usePosFolioPolicies()
  const { mutateAsync: revocar } = useRevokeFolios()
  const { confirm, modalProps } = useConfirmModal()
  const [asignando, setAsignando] = useState(false)
  const [politicas, setPoliticas] = useState(false)
  const [verTodos, setVerTodos] = useState(false)

  const ranges = data?.ranges ?? []
  const visibles = verTodos ? ranges : ranges.filter((r) => r.status === 'active')
  const umbral = (code: number) => policies.find((p) => p.document_type_code === code)?.alert_threshold ?? 0

  const pedirRevocacion = (r: PosFolioRange) => confirm({
    message: `¿Revocar los folios ${fmt(r.folio_from)}–${fmt(r.folio_to)} de ${r.terminal_code}? `
      + `Los ${fmt(r.available)} sin usar quedan quemados: no se entregan a nadie más y hay que anularlos ante el SII. `
      + 'El terminal deja de usarlos en su siguiente conexión. No se puede deshacer.',
    confirmLabel: 'Revocar',
    onConfirm: async () => {
      try {
        await revocar(r.id)
        toast.success('Rango revocado')
      } catch (e) {
        toast.error(extractErrorMessage(e, 'No se pudo revocar el rango.'))
      }
    },
  })

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <label className="flex items-center gap-2 text-sm text-content-muted">
          <input type="checkbox" checked={verTodos} onChange={(e) => setVerTodos(e.target.checked)} />
          Incluir agotados y revocados
        </label>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => setPoliticas(true)}>
            <SlidersHorizontal size={14} /> Política
          </Button>
          {can('pos.folios', 'assign') && (
            <Button onClick={() => setAsignando(true)}>
              <Plus size={14} /> Asignar folios
            </Button>
          )}
        </div>
      </div>

      <DataTable<PosFolioRange>
        rows={visibles}
        rowKey={(r) => r.id}
        loading={isLoading}
        empty={<EmptyState
          title={verTodos ? 'No hay rangos asignados.' : 'No hay rangos vigentes.'}
          hint="Un terminal necesita folios propios para emitir sin conexión. Se asignan acá, desde el CAF vigente."
          size="sm" />}
        columns={[
          { key: 'terminal', header: 'Terminal', render: (r) => (
            <span><span className="font-mono">{r.terminal_code}</span>
              <span className="block text-xs text-content-subtle">{r.store_name}</span></span>
          ) },
          { key: 'type', header: 'Documento', render: (r) => tipo(r.document_type_code) },
          { key: 'range', header: 'Rango', render: (r) => (
            <span className="font-mono tabular-nums">{fmt(r.folio_from)}–{fmt(r.folio_to)}</span>
          ) },
          { key: 'available', header: 'Disponibles', render: (r) => {
            const total = r.folio_to - r.folio_from + 1
            const bajo = r.status === 'active' && r.available <= umbral(r.document_type_code)
            return (
              <span className={`tabular-nums ${bajo ? 'text-warning-content font-medium' : 'text-content'}`}
                title={bajo ? 'Bajo el umbral de aviso' : undefined}>
                {fmt(r.available)} <span className="text-content-subtle">de {fmt(total)}</span>
              </span>
            )
          } },
          { key: 'reported', header: 'Último reporte', render: (r) => r.reported_at
            ? <span title={`Próximo folio informado: ${r.reported_next_folio}`}>{fmtDateTime(r.reported_at)}</span>
            : <span className="text-content-subtle">Sin reportes</span> },
          { key: 'status', header: 'Estado', render: (r) => <StatusBadge status={r.status} catalog={POS_FOLIO_RANGE_STATUS} /> },
          { key: 'actions', header: '', render: (r) => can('pos.folios', 'approve') && r.status === 'active' ? (
            <div className="flex justify-end">
              <button onClick={() => pedirRevocacion(r)} title="Revocar el rango" aria-label={`Revocar el rango de ${r.terminal_code}`}
                className="p-1.5 rounded-lg hover:bg-soft text-content-subtle hover:text-danger-content">
                <Ban size={14} />
              </button>
            </div>
          ) : null },
        ]}
      />

      {asignando && (
        <AsignarModal
          pool={data?.pool ?? {}}
          ranges={ranges}
          policies={policies}
          onClose={() => setAsignando(false)}
          onSaved={(r) => {
            toast.success(`Folios ${fmt(r.folio_from)}–${fmt(r.folio_to)} asignados a ${r.terminal_code}`)
            setAsignando(false)
          }}
        />
      )}
      {politicas && (
        <PoliticaModal policies={policies} editable={can('pos.folios', 'assign')} onClose={() => setPoliticas(false)} />
      )}

      <ConfirmModal {...modalProps} />
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </>
  )
}

function AsignarModal({ pool, ranges, policies, onClose, onSaved }: {
  pool: Record<string, FolioPool>
  ranges: PosFolioRange[]
  policies: PosFolioPolicy[]
  onClose: () => void
  onSaved: (r: PosFolioRange) => void
}) {
  const { data: terminals = [] } = usePosTerminals()
  const { mutate, isPending } = useAssignFolios()
  const [terminalId, setTerminalId] = useState('')
  const [docType, setDocType] = useState<number | null>(null)
  const [cantidad, setCantidad] = useState('')
  const [desdeTexto, setDesdeTexto] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Solo terminales que pueden operar: a uno revocado o desactivado no se le entregan folios.
  const operables = terminals.filter((t) => t.status === 'active' || t.status === 'pending')
  const terminal = operables.find((t) => t.id === terminalId)
  const tipos = terminal?.enabled_document_types ?? []
  const tipoElegido = docType ?? (tipos.length === 1 ? tipos[0] : null)
  const porDefecto = policies.find((p) => p.document_type_code === tipoElegido)?.buffer_target ?? 100
  const n = Number(cantidad || porDefecto)
  const origen = tipoElegido != null ? pool[String(tipoElegido)] : undefined
  const desde = desdeTexto ? Number(desdeTexto) : null
  // Un agente continúa la numeración de su último rango del tipo (la del sistema del cliente).
  const continuacion = useMemo(() => {
    if (terminal?.kind !== 'agente' || tipoElegido == null) return null
    const propios = ranges.filter((r) => r.terminal === terminal.id && r.document_type_code === tipoElegido)
    return propios.length ? Math.max(...propios.map((r) => r.folio_to)) + 1 : null
  }, [ranges, terminal, tipoElegido])

  // Lo que saldría, para que quien asigna lo vea antes de confirmar.
  const vista = useMemo(() => {
    if (tipoElegido == null) return null
    const inicio = desde ?? continuacion
    // Desde un folio elegido: el CAF y los rangos vecinos los revisa Clobi al asignar.
    if (inicio != null) return { sinCaf: false as const, desde: inicio, hasta: inicio + n - 1, recortado: false, aRevisar: true }
    // Sin entrada en el pool = el tipo no tiene folios configurados ni CAF: hay
    // que decirlo, no dejar el botón deshabilitado sin explicación.
    if (!origen || !origen.caf_valid_to || origen.available_in_caf <= 0) return { sinCaf: true as const }
    const hasta = Math.min(origen.next_folio + n - 1, origen.caf_valid_to)
    return { sinCaf: false as const, desde: origen.next_folio, hasta, recortado: hasta < origen.next_folio + n - 1, aRevisar: false }
  }, [origen, tipoElegido, n, desde, continuacion])

  const enviar = () => {
    setError(null)
    if (!terminal || tipoElegido == null) {
      setError('Falta elegir el terminal y el tipo de documento.')
      return
    }
    mutate({ terminal: terminal.id, document_type_code: tipoElegido, quantity: n, ...(desde ? { folio_from: desde } : {}) }, {
      onSuccess: onSaved,
      onError: (e) => setError(extractErrorMessage(e, 'No se pudieron asignar los folios.')),
    })
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Asignar folios a un terminal"
      size="md"
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button onClick={enviar} isPending={isPending} disabled={!vista || vista.sinCaf}>Asignar</Button>
        </>
      }
    >
      <div className="space-y-3">
        <SelectField dense label="Terminal" required placeholder="Seleccionar el terminal"
          value={terminalId}
          onChange={(e) => { setTerminalId(e.target.value); setDocType(null) }}
          options={operables.map((t) => ({ value: t.id, label: `${t.code} · ${t.store_name}` }))}
          hint={operables.length === 0 ? 'No hay terminales que puedan operar.' : undefined} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SelectField dense label="Documento" required placeholder="Seleccionar el tipo"
            value={tipoElegido != null ? String(tipoElegido) : ''}
            onChange={(e) => setDocType(e.target.value ? Number(e.target.value) : null)}
            disabled={!terminal}
            options={tipos.map((c) => ({ value: String(c), label: tipo(c) }))} />
          <TextField dense label="Cantidad" placeholder={String(porDefecto)} value={cantidad}
            onChange={(e) => setCantidad(e.target.value.replace(/\D/g, ''))}
            inputProps={{ inputMode: 'numeric' }} />
        </div>
        <TextField dense label="Desde el folio (opcional)" value={desdeTexto}
          placeholder={continuacion != null ? String(continuacion) : origen ? String(origen.next_folio) : ''}
          onChange={(e) => setDesdeTexto(e.target.value.replace(/\D/g, ''))}
          inputProps={{ inputMode: 'numeric' }}
          hint="Para un sistema que numera sus documentos: el folio en que va. Vacío, el siguiente libre." />
        {vista && (vista.sinCaf ? (
          <p className="text-sm text-danger-content">
            No hay un CAF vigente con folios libres para este tipo. Hay que cargar o pedir folios al SII en Configuración → Folios.
          </p>
        ) : (
          <p className="text-sm text-content-muted">
            Se entregan los folios <strong className="text-content tabular-nums">{fmt(vista.desde)}–{fmt(vista.hasta)}</strong>.
            {vista.recortado && ' El CAF vigente termina antes: un rango no cruza dos CAF, así que se entregan menos.'}
            {vista.aRevisar
              ? ' Si el CAF termina antes o el rango alcanza el de otro terminal, se entregan menos; si alguno ya se usó en un documento, no se asigna.'
              : ' La emisión desde la web sigue con los folios siguientes.'}
          </p>
        ))}
        {error && <p className="text-sm text-danger-content">{error}</p>}
      </div>
    </Modal>
  )
}

function PoliticaModal({ policies, editable, onClose }: {
  policies: PosFolioPolicy[]
  editable: boolean
  onClose: () => void
}) {
  const { mutateAsync, isPending } = useSaveFolioPolicy()
  const [filas, setFilas] = useState(policies)
  const [error, setError] = useState<string | null>(null)
  const cambiar = (code: number, campo: keyof PosFolioPolicy, valor: number | boolean) =>
    setFilas((fs) => fs.map((f) => f.document_type_code === code ? { ...f, [campo]: valor } : f))

  const guardar = async () => {
    setError(null)
    try {
      const cambiadas = filas.filter((f) => {
        const o = policies.find((p) => p.document_type_code === f.document_type_code)
        return JSON.stringify(o) !== JSON.stringify(f)
      })
      for (const f of cambiadas) await mutateAsync(f)
      onClose()
    } catch (e) {
      setError(extractErrorMessage(e, 'No se pudo guardar la política.'))
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Política de folios por tipo de documento"
      size="lg"
      actions={editable ? (
        <>
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button onClick={guardar} isPending={isPending}>Guardar</Button>
        </>
      ) : <Button onClick={onClose}>Cerrar</Button>}
    >
      <p className="text-sm text-content-muted mb-3">
        «Por asignación» es lo que se entrega cada vez y el colchón que repone la asignación automática.
        Con «Avisar con» folios o menos en un terminal, se avisa a quienes pueden asignar.
      </p>
      <DataTable<PosFolioPolicy>
        rows={filas}
        rowKey={(f) => String(f.document_type_code)}
        columns={[
          { key: 'type', header: 'Documento', render: (f) => tipo(f.document_type_code) },
          { key: 'buffer', header: 'Por asignación', render: (f) => (
            <input type="number" min={1} disabled={!editable} value={f.buffer_target}
              aria-label={`Folios por asignación de ${tipo(f.document_type_code)}`}
              onChange={(e) => cambiar(f.document_type_code, 'buffer_target', Number(e.target.value))}
              className="cl-input w-24" />
          ) },
          { key: 'threshold', header: 'Avisar con', render: (f) => (
            <input type="number" min={0} disabled={!editable} value={f.alert_threshold}
              aria-label={`Umbral de aviso de ${tipo(f.document_type_code)}`}
              onChange={(e) => cambiar(f.document_type_code, 'alert_threshold', Number(e.target.value))}
              className="cl-input w-24" />
          ) },
          { key: 'auto', header: 'Automática', render: (f) => (
            <input type="checkbox" disabled={!editable} checked={f.auto_assign}
              aria-label={`Asignación automática de ${tipo(f.document_type_code)}`}
              onChange={(e) => cambiar(f.document_type_code, 'auto_assign', e.target.checked)} />
          ) },
        ]}
      />
      <p className="text-xs text-content-subtle mt-3">
        Con la asignación automática, el terminal pide su próximo rango solo cuando tiene conexión,
        sin esperar a que alguien lo asigne.
      </p>
      {error && <p className="text-sm text-danger-content mt-2">{error}</p>}
    </Modal>
  )
}
